<template>
  <b-modal
    v-model="isVisible"
    size="xl"
    title="Visualizar correo"
    hide-footer
    scrollable
    @hidden="handleClose"
  >
    <template v-if="selectedEmail">
      <div class="email-viewer">
        <!-- Header del correo -->
        <div class="email-header border-bottom pb-3 mb-3">
          <h5 class="mb-2">{{ selectedEmail.subject }}</h5>
          <div class="d-flex flex-wrap">
            <div class="mr-4 mb-1">
              <strong>De:</strong>
              <span class="ml-1">{{ fromDisplay }}</span>
            </div>
            <div class="mr-4 mb-1">
              <strong>Para:</strong>
              <span class="ml-1">{{ toDisplay }}</span>
            </div>
            <div v-if="ccDisplay" class="mr-4 mb-1">
              <strong>CC:</strong>
              <span class="ml-1">{{ ccDisplay }}</span>
            </div>
            <div class="mb-1">
              <strong>Fecha:</strong>
              <span class="ml-1">{{ formattedDate }}</span>
            </div>
          </div>
        </div>

        <!-- Adjuntos -->
        <div v-if="hasAttachments" class="email-attachments border-bottom pb-3 mb-3">
          <strong>
            <feather-icon icon="PaperclipIcon" size="14" class="mr-1" />
            Adjuntos ({{ selectedEmail.attachments.length }}):
          </strong>
          <div class="mt-2 d-flex flex-wrap">
            <div
              v-for="attachment in selectedEmail.attachments"
              :key="attachment.id"
              class="attachment-item mr-2 mb-2 p-2 border rounded d-flex align-items-center"
              @click="downloadAttachment(attachment)"
            >
              <feather-icon icon="DownloadIcon" size="16" class="mr-2 text-primary" />
              <div>
                <div class="small font-weight-bold">{{ attachment.name }}</div>
                <div class="small text-muted">{{ formatSize(attachment.contentLength) }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Contenido del correo -->
        <div class="email-body">
          <div v-if="loadingContent" class="text-center py-4">
            <b-spinner variant="primary" />
            <p class="mt-2 text-muted">Cargando contenido...</p>
          </div>
          <div v-else-if="emailHtmlContent" ref="shadowHost" class="email-html-content" />
          <div v-else-if="emailTextContent" class="email-text-content">
            <pre class="mb-0" style="white-space: pre-wrap; font-family: inherit;">{{ emailTextContent }}</pre>
          </div>
          <div v-else class="text-center py-4 text-muted">
            <feather-icon icon="AlertCircleIcon" size="24" class="mb-2" />
            <p>No se pudo cargar el contenido del correo</p>
          </div>
        </div>

        <!-- Info S3 -->
        <div class="email-metadata border-top pt-3 mt-3">
          <small class="text-muted">
            <strong>Object Key:</strong> {{ selectedEmail.objectKey }}
          </small>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { ioDownloadContentPrivateFile, ioDownloadPrivateFile } from '@/febos/servicios/api/herramientas.api';

export default {
  name: 'ReceivedEmailViewer',
  data() {
    return {
      loadingContent: false,
      emailHtmlContent: null,
      emailTextContent: null
    };
  },
  computed: {
    ...mapGetters('ReceivedEmails', ['selectedEmail', 'showEmailViewer']),
    isVisible: {
      get() {
        return this.showEmailViewer;
      },
      set(value) {
        if (!value) this.handleClose();
      }
    },
    fromDisplay() {
      if (!this.selectedEmail || !this.selectedEmail.from) return '';
      return this.selectedEmail.from.join(', ');
    },
    toDisplay() {
      const recipients = this.selectedEmail.to || this.selectedEmail.tos || [];
      return recipients.join(', ');
    },
    ccDisplay() {
      if (!this.selectedEmail || !this.selectedEmail.cc || this.selectedEmail.cc.length === 0) {
        return null;
      }
      return this.selectedEmail.cc.join(', ');
    },
    formattedDate() {
      if (!this.selectedEmail || !this.selectedEmail.timestamp) return '';
      const ts = this.selectedEmail.timestamp;
      const date = new Date(ts.endsWith('Z') ? ts : `${ts}Z`);
      return date.toLocaleString('es-CL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    hasAttachments() {
      return this.selectedEmail
        && this.selectedEmail.hasAttachments
        && this.selectedEmail.attachments
        && this.selectedEmail.attachments.length > 0;
    }
  },
  watch: {
    selectedEmail(email) {
      if (email) {
        this.loadEmailContent(email);
      }
    },
    emailHtmlContent(html) {
      if (html) {
        this.$nextTick(() => {
          this.renderHtmlInShadow(html);
        });
      }
    }
  },
  methods: {
    ...mapActions('ReceivedEmails', ['closeEmailViewer']),
    handleClose() {
      this.emailHtmlContent = null;
      this.emailTextContent = null;
      this.closeEmailViewer();
    },
    async loadEmailContent(email) {
      this.emailHtmlContent = null;
      this.emailTextContent = null;

      const bucket = 'febos-io';
      if (email.html && email.html.key) {
        await this.fetchContent(`${bucket}/${email.html.key}`, 'html');
      } else if (email.text && email.text.key) {
        await this.fetchContent(`${bucket}/${email.text.key}`, 'text');
      } else if (email.content && email.content.key) {
        await this.fetchContent(`${bucket}/${email.content.key}`, 'text');
      }
    },
    async fetchContent(key, type) {
      try {
        this.loadingContent = true;
        const content = await ioDownloadContentPrivateFile(key);
        if (type === 'html') {
          this.emailHtmlContent = content;
        } else {
          this.emailTextContent = content;
        }
      } catch (error) {
        console.error('Error al cargar contenido del correo:', error);
        this.emailTextContent = 'No se pudo cargar el contenido del correo.';
      } finally {
        this.loadingContent = false;
      }
    },
    formatSize(bytes) {
      if (!bytes) return '0 B';
      const units = ['B', 'KB', 'MB', 'GB'];
      let unitIndex = 0;
      let size = bytes;
      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex++;
      }
      return `${size.toFixed(1)} ${units[unitIndex]}`;
    },
    renderHtmlInShadow(html) {
      const host = this.$refs.shadowHost;
      if (!host) return;
      if (!host.shadowRoot) {
        host.attachShadow({ mode: 'open' });
      }
      host.shadowRoot.innerHTML = html;
    },
    async downloadAttachment(attachment) {
      try {
        const path = `febos-io/${attachment.key}`;
        const response = await ioDownloadPrivateFile({ path, nombre: attachment.name });
        const link = document.createElement('a');
        link.href = response.data.url;
        link.download = attachment.name;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Error al descargar adjunto:', error);
        this.$bvToast.toast('No se pudo descargar el adjunto', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }
    }
  }
};
</script>

<style scoped>
.email-viewer {
  min-height: 300px;
}
.email-html-content {
  max-height: 60vh;
  overflow-y: auto;
  padding: 10px;
  border: 1px solid #eee;
  border-radius: 4px;
  background: #fafafa;
}
.email-text-content {
  max-height: 60vh;
  overflow-y: auto;
  padding: 10px;
  border: 1px solid #eee;
  border-radius: 4px;
  background: #fafafa;
}
.attachment-item {
  background: #f8f9fa;
  cursor: pointer;
  transition: background-color 0.2s;
}
.attachment-item:hover {
  background: #e2e6ea;
}
</style>
